import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import type { Order, OrderLine, Party, Product } from '@prody/shared';
import {
  ORDER_TRANSITIONS, computeOrderTotals, hasRole, lineNet, primaryChannel,
} from '@prody/shared';
import {
  Button, Code, ConfirmDialog, CrateHeader, EmptyState, FactList, IconChevronRight,
  IconPrint, ListPane, Metric, PageHeader, Section, Select, Stamp, Toolbar,
  Workbench, toast,
} from '~/design-system';
import { businessPath, path } from '~/app/navigation';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useOrder, useParties, useProducts, useRoutes, useShipments, useUpdateOrder,
} from '~/platform/data/queries';
import {
  ORDER_TONE, isTerminalOrderStatus, orderHandlingClass, productName,
} from '~/platform/domain/presentation';
import { ShortfallDialog } from './OrderManifest';
import { orderCogsMinor, orderMeta } from './meta';
import './orders.css';

/* ==================================================================
   THE ORDER, OPENED.

   The list answers "how is the day going"; this page answers every
   question one order can raise: what was asked for, what is going out,
   who covers each line, what it comes to, where it is on the van, and
   what was said about it. It is the screen you print an albarán from
   and the screen you edit from.
   ================================================================== */

export default function OrderDetailPage() {
  const { t, i18n } = useTranslation(['orders', 'common', 'logistics']);
  const { business, format, can } = useBusiness();
  const navigate = useNavigate();
  const { orderId } = useParams({ strict: false }) as { orderId?: string };

  const { data: order, isLoading } = useOrder(business.id, orderId ?? null);
  const { data: products = [] } = useProducts(business.id);
  const { data: parties = [] } = useParties(business.id);
  const updateOrder = useUpdateOrder(business.id);

  const [shortfallLine, setShortfallLine] = useState<OrderLine | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const base = businessPath(business.slug);

  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );
  const suppliers = useMemo(
    () => parties.filter((p) => hasRole(p, 'supplier')),
    [parties],
  );
  const customer = order ? parties.find((p) => p.id === order.customerId) ?? null : null;

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  /* The albarán and route this order rides on, when they exist. */
  const { data: shipments = [] } = useShipments(business.id, order?.deliveryDate ?? '');
  const { data: routes = [] } = useRoutes(business.id, order?.deliveryDate ?? '');
  const shipment = order
    ? shipments.find((s) => s.id === order.shipmentId || s.orderIds.includes(order.id)) ?? null
    : null;
  const route = shipment?.routeId ? routes.find((r) => r.id === shipment.routeId) ?? null : null;

  if (isLoading) {
    return (
      <Workbench>
        <div style={{ padding: 'var(--s-7)' }} aria-busy="true">
          <span className="hatch" style={{ display: 'block', blockSize: '1.4em', maxInlineSize: 260 }} />
        </div>
      </Workbench>
    );
  }

  if (!order) {
    return (
      <Workbench>
        <ListPane>
          <EmptyState
            title={t('common:errors.notFound')}
            body={t('common:errors.notFoundBody')}
            action={
              <Button variant="primary" onClick={() => void navigate({ to: path(`${base}/orders`) })}>
                {t('common:actions.back')}
              </Button>
            }
          />
        </ListPane>
      </Workbench>
    );
  }

  const totals = computeOrderTotals(order, taxBpsFor);
  const nextStatuses = ORDER_TRANSITIONS[order.status];
  const canEdit = can('order', 'update') && !isTerminalOrderStatus(order.status);

  /* The commercial picture: what it cost us, what it earns, what is
     still owed. All three come off the live feed's own numbers. */
  const meta = orderMeta(order);
  const cogsMinor = orderCogsMinor(order);
  const profitMinor = cogsMinor === null ? null : totals.net.minor - cogsMinor;
  const balanceMinor = meta.paidMinor === null ? null : totals.gross.minor - meta.paidMinor;
  const money = (minor: number) =>
    format.money({ minor, currency: order.currency });
  const lineCost = (line: OrderLine): number | null =>
    meta.lineCosts?.[line.id as string] ?? null;
  /** Only worth showing when it differs from the billed quantity.
      Compared loosely: the formatter may use a narrow no-break space
      where the feed used a plain one. */
  const lineRequest = (line: OrderLine): string | null => {
    const raw = meta.lineRequests?.[line.id as string] ?? null;
    if (!raw) return null;
    const flat = (s: string) => s.replace(/[\s  ]+/g, '').toLowerCase();
    return flat(raw) === flat(format.quantity(line.ordered)) ? null : raw;
  };

  const address = customer?.addresses[0];
  const addressLabel =
    ((order.custom as Record<string, unknown>)['addressLabel'] as string | undefined) ||
    (address ? `${address.line1}, ${address.city}` : null);

  const advance = (to: Order['status']) => {
    updateOrder.mutate(
      { orderId: order.id, patch: { status: to } },
      {
        onSuccess: () =>
          toast.ok(`${t('number')} ${order.number}`, { detail: t(`status.${to}`) }),
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  const assignLineSupplier = (line: OrderLine, supplierId: string) => {
    const supplier = suppliers.find((s) => s.id === supplierId) ?? null;
    updateOrder.mutate(
      {
        orderId: order.id,
        patch: {
          lines: order.lines.map((l) =>
            l.id === line.id
              ? {
                  ...l,
                  supplierId: (supplier?.id ?? null) as OrderLine['supplierId'],
                  supplierName: supplier?.name ?? null,
                }
              : l,
          ),
        },
      },
      {
        onSuccess: () =>
          toast.ok(t('detail.supplierAssigned', { product: line.productName }), {
            detail: supplier?.name ?? t('detail.unassigned'),
          }),
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  /** Line supplier as the picker sees it: the override, or the product default. */
  const effectiveSupplierId = (line: OrderLine): string => {
    if (line.supplierId) return line.supplierId as string;
    const fallback = productsById.get(line.productId)?.defaultSupplierId;
    return (fallback as string | null | undefined) ?? '';
  };

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={
          <span className="row-baseline">
            <span className="pd-odetail__number">{order.number}</span>
            <span className="pd-odetail__client">
              {meta.tradeName ? <Code>{meta.tradeName}</Code> : null}
              {order.customerName}
            </span>
          </span>
        }
        actions={
          <>
            <Button variant="quiet" onClick={() => void navigate({ to: path(`${base}/orders`) })}>
              {t('common:actions.back')}
            </Button>
            <Button
              variant="quiet"
              icon={<IconPrint />}
              onClick={() => window.print()}
            >
              {t('common:actions.print')}
            </Button>
            {canEdit ? (
              <Button
                variant="primary"
                onClick={() => void navigate({ to: path(`${base}/orders/${order.id}/edit`) })}
              >
                {t('common:actions.edit')}
              </Button>
            ) : null}
          </>
        }
      />

      <Toolbar />

      <ListPane>
        <div className="pd-odetail">
          <CrateHeader
            handlingClass={orderHandlingClass(order, productsById)}
            code={order.number}
            title={order.customerName}
            subtitle={t(`channels.${order.channel}`)}
            stamp={
              <Stamp
                tone={ORDER_TONE[order.status]}
                weight={isTerminalOrderStatus(order.status) ? 'solid' : 'outline'}
              >
                {t(`status.${order.status}`)}
              </Stamp>
            }
            facts={[
              { label: t('deliveryDate'), value: format.date(order.deliveryDate, 'weekday') },
              { label: t('common:labels.window'), value: format.timeWindow(order.deliveryWindow) },
              { label: t('detail.lines'), value: <span className="num">{order.lines.length}</span> },
              {
                label: t('fillRate'),
                value: totals.fillRate === null ? '—' : `${Math.round(totals.fillRate * 100)}%`,
              },
              {
                label: t('common:labels.total'),
                value: <span className="num">{format.money(totals.gross)}</span>,
              },
            ]}
          />

          {/* The commercial strip. What it sold for, what it cost, what
              it earns and what is still owed — the four numbers this
              screen was always asked for and used to hide. */}
          <div className="pd-odetail__figures">
            <Metric label={t('columns.sold')} value={money(totals.gross.minor)} />
            {cogsMinor !== null ? (
              <Metric label={t('columns.cogs')} value={money(cogsMinor)} />
            ) : null}
            {profitMinor !== null ? (
              <Metric
                label={t('columns.profit')}
                value={money(profitMinor)}
                tone={profitMinor >= 0 ? 'ok' : 'alert'}
                footnote={
                  totals.net.minor > 0
                    ? `${Math.round((profitMinor / totals.net.minor) * 100)}%`
                    : undefined
                }
              />
            ) : null}
            {meta.paidMinor !== null ? (
              <Metric label={t('columns.paid')} value={money(meta.paidMinor)} />
            ) : null}
            {balanceMinor !== null ? (
              <Metric
                label={t('columns.balance')}
                value={money(balanceMinor)}
                tone={balanceMinor > 0 ? 'alert' : 'ok'}
                footnote={balanceMinor <= 0 ? t('detail.settled') : undefined}
              />
            ) : null}
          </div>

          <div className="pd-odetail__grid">
            <div className="pd-odetail__main">
              {/* ---- Status ---- */}
              {can('order', 'update') && nextStatuses.length > 0 ? (
                <Section title={t('common:labels.status')}>
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    {nextStatuses
                      .filter((next) => next !== 'cancelled')
                      .map((next, i) => (
                        <Button
                          key={next}
                          variant={i === 0 ? 'primary' : 'default'}
                          size="sm"
                          busy={updateOrder.isPending}
                          onClick={() => advance(next)}
                        >
                          {t(`status.${next}`)}
                        </Button>
                      ))}
                    {nextStatuses.includes('cancelled') ? (
                      <Button variant="danger" size="sm" onClick={() => setConfirmCancel(true)}>
                        {t('status.cancelled')}
                      </Button>
                    ) : null}
                  </div>
                </Section>
              ) : null}

              {/* ---- Lines ---- */}
              <Section title={t('detail.lines')}>
                <table className="pd-lines">
                  <thead>
                    <tr>
                      <th className="stencil" aria-hidden="true" />
                      <th className="stencil">{t('common:labels.product')}</th>
                      <th className="stencil">{t('common:labels.supplier')}</th>
                      <th className="stencil right">{t('detail.ordered')}</th>
                      <th className="stencil right">{t('detail.picked')}</th>
                      <th className="stencil right">{t('common:labels.price')}</th>
                      {cogsMinor !== null ? (
                        <>
                          <th className="stencil right">{t('common:labels.cost')}</th>
                          <th className="stencil right">{t('common:labels.margin')}</th>
                        </>
                      ) : null}
                      <th className="stencil right">{t('common:labels.total')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {order.lines.map((row) => {
                      const product = productsById.get(row.productId);
                      return (
                        <tr
                          key={row.id}
                          data-hc={product?.handlingClass ?? 'neutral'}
                          data-short={row.shortfall ? true : undefined}
                        >
                          <td className="pd-lines__band">
                            <span className="pd-band" />
                          </td>

                          <td>
                            <div className="pd-lines__name">
                              <span className="truncate">
                                {product
                                  ? productName(product, i18n.resolvedLanguage ?? 'en')
                                  : row.productName}
                              </span>
                              <Code>{row.sku}</Code>
                            </div>
                            {/* What the customer actually asked for. Kept
                                visible because "3 und" priced as 0,99 kg
                                is the single most disputed line on any
                                invoice in this trade. */}
                            {lineRequest(row) ? (
                              <p className="pd-lines__request">
                                <span className="stencil">{t('detail.requested')}</span>
                                <span className="num">{lineRequest(row)}</span>
                              </p>
                            ) : null}
                            {row.note ? <p className="pd-lines__note">{row.note}</p> : null}
                            {row.shortfall ? (
                              <p className="pd-lines__shortfall">
                                <Stamp tone="alert" size="sm">
                                  {t('detail.shortfall')}
                                </Stamp>
                                <span className="num">{format.quantity(row.shortfall.quantity)}</span>
                                <span className="dim">· {t(`shortfallReason.${row.shortfall.reason}`)}</span>
                              </p>
                            ) : null}
                          </td>

                          <td className="pd-lines__supplier">
                            {canEdit ? (
                              <Select
                                value={effectiveSupplierId(row)}
                                aria-label={t('common:labels.supplier')}
                                onChange={(e) => assignLineSupplier(row, e.target.value)}
                              >
                                <option value="">{t('detail.unassigned')}</option>
                                {suppliers.map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                              </Select>
                            ) : (
                              <span className="t-small dim">
                                {supplierLabel(row, productsById, suppliers) ?? t('detail.unassigned')}
                              </span>
                            )}
                          </td>

                          <td className="right num">{format.quantity(row.ordered)}</td>
                          <td className="right num">
                            {row.picked ? format.quantity(row.picked) : '—'}
                          </td>
                          <td className="right num">{format.money(row.unitPrice, { bare: true })}</td>

                          {cogsMinor !== null ? (
                            <>
                              <td className="right num faint">
                                {lineCost(row) === null
                                  ? '—'
                                  : format.money(
                                      { minor: lineCost(row)!, currency: order.currency },
                                      { bare: true },
                                    )}
                              </td>
                              <td className="right num">
                                {(() => {
                                  const cost = lineCost(row);
                                  if (cost === null || cost === 0) return <span className="faint">—</span>;
                                  const margin = Math.round(
                                    ((row.unitPrice.minor - cost) / row.unitPrice.minor) * 100,
                                  );
                                  return (
                                    <span data-tone={margin < 0 ? 'alert' : undefined}
                                      className={margin < 0 ? 'pd-lines__margin-neg' : undefined}>
                                      {margin}%
                                    </span>
                                  );
                                })()}
                              </td>
                            </>
                          ) : null}

                          <td className="right num">{format.money(lineNet(row), { bare: true })}</td>

                          <td className="pd-lines__actions">
                            {canEdit && !row.shortfall ? (
                              <Button variant="quiet" size="sm" onClick={() => setShortfallLine(row)}>
                                {t('detail.recordShortfall')}
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Section>

              {/* ---- Totals ---- */}
              <Section>
                <dl className="pd-totals">
                  <div>
                    <dt className="stencil">{t('common:labels.net')}</dt>
                    <dd className="num">{format.money(totals.net)}</dd>
                  </div>
                  {totals.discount.minor !== 0 ? (
                    <div>
                      <dt className="stencil">{t('common:labels.discount')}</dt>
                      <dd className="num">{format.money(totals.discount)}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="stencil">{t('common:labels.tax')}</dt>
                    <dd className="num">{format.money(totals.tax)}</dd>
                  </div>
                  <div className="pd-totals__grand">
                    <dt className="stencil">{t('common:labels.total')}</dt>
                    <dd className="num" style={{ fontSize: 'var(--t-title)' }}>
                      {format.money(totals.gross)}
                    </dd>
                  </div>
                </dl>
              </Section>
            </div>

            <aside className="pd-odetail__side">
              {/* ---- Customer ---- */}
              <Section title={t('common:labels.customer')}>
                <FactList
                  columns={1}
                  items={[
                    {
                      label: t('columns.tradeName'),
                      value: (
                        <span className="row-baseline">
                          {meta.tradeName || customer?.code ? (
                            <Code>{meta.tradeName ?? customer!.code}</Code>
                          ) : null}
                          <span>{order.customerName}</span>
                        </span>
                      ),
                    },
                    ...(meta.contactName
                      ? [{ label: t('columns.contact'), value: meta.contactName }]
                      : []),
                    ...(meta.phone || (customer && primaryChannel(customer))
                      ? [
                          {
                            label: t('common:channel.phone'),
                            value: (
                              <a
                                className="num pd-odetail__tel"
                                href={`tel:${meta.phone ?? primaryChannel(customer!)!.address}`}
                              >
                                {meta.phone ?? primaryChannel(customer!)!.address}
                              </a>
                            ),
                          },
                        ]
                      : []),
                    ...(addressLabel
                      ? [{ label: t('detail.address'), value: addressLabel }]
                      : []),
                    ...(meta.paymentType
                      ? [{ label: t('columns.payment'), value: meta.paymentType }]
                      : []),
                    ...(meta.terminal
                      ? [{ label: t('detail.terminal'), value: <Code>{meta.terminal}</Code> }]
                      : []),
                  ]}
                />
              </Section>

              {/* ---- Delivery ---- */}
              <Section title={t('deliveryDate')}>
                <FactList
                  columns={1}
                  items={[
                    { label: t('common:labels.date'), value: format.date(order.deliveryDate, 'medium') },
                    { label: t('common:labels.window'), value: format.timeWindow(order.deliveryWindow) },
                    ...(shipment
                      ? [
                          {
                            label: t('detail.shipment'),
                            value: (
                              <span className="row-baseline">
                                <Code>{shipment.number}</Code>
                                <Stamp tone="info" size="sm">
                                  {t(`logistics:stopStatus.${shipment.status}`)}
                                </Stamp>
                              </span>
                            ),
                          },
                        ]
                      : []),
                    ...(route
                      ? [{ label: t('logistics:route'), value: route.name }]
                      : []),
                  ]}
                />
              </Section>

              {/* ---- Notes ---- */}
              {order.customerNote || order.internalNote ? (
                <Section title={t('common:labels.notes')}>
                  <FactList
                    columns={1}
                    items={[
                      ...(order.customerNote
                        ? [{ label: t('common:labels.customerNote'), value: order.customerNote }]
                        : []),
                      ...(order.internalNote
                        ? [{ label: t('common:labels.internalNote'), value: order.internalNote }]
                        : []),
                    ]}
                  />
                </Section>
              ) : null}

              {/* ---- Record ---- */}
              <Section title={t('detail.timeline')}>
                <FactList
                  columns={1}
                  items={[
                    { label: t('detail.placedAt'), value: format.dateTime(order.placedAt) },
                    { label: t('common:labels.updated'), value: format.dateTime(order.updatedAt) },
                    { label: t('channel'), value: t(`channels.${order.channel}`) },
                    {
                      label: t('common:labels.assignedTo'),
                      value: order.assignedToId ?? t('filters.unassigned'),
                    },
                  ]}
                />
              </Section>

              {order.invoiceId ? (
                <Section title={t('detail.documents')}>
                  <p className="row-baseline t-small">
                    <IconChevronRight />
                    <Code>{order.invoiceId}</Code>
                  </p>
                </Section>
              ) : null}
            </aside>
          </div>
        </div>
      </ListPane>

      {shortfallLine ? (
        <ShortfallDialog line={shortfallLine} order={order} onClose={() => setShortfallLine(null)} />
      ) : null}

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title={t('cancelTitle', { number: order.number })}
        body={t('cancelBody')}
        confirmLabel={t('cancelConfirm')}
        cancelLabel={t('common:actions.back')}
        busy={updateOrder.isPending}
        onConfirm={() => {
          setConfirmCancel(false);
          advance('cancelled');
        }}
      />
    </Workbench>
  );
}

const supplierLabel = (
  line: OrderLine,
  productsById: Map<string, Product>,
  suppliers: Party[],
): string | null => {
  if (line.supplierName) return line.supplierName;
  const fallback = productsById.get(line.productId)?.defaultSupplierId;
  if (!fallback) return null;
  return suppliers.find((s) => s.id === fallback)?.name ?? null;
};
